import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { Loading, Empty, useToast, Modal, Field, PageHeader } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import ImagePicker from '../../components/ImagePicker.jsx';
import ServiceIcon from '../../components/ServiceIcon.jsx';
import { serviceIconUrl } from '../../lib/format.js';

export default function Services() {
  const toast = useToast();
  const [services, setServices] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [iconFile, setIconFile] = useState(null); // pending image for add/edit
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  // The admin manages hidden services too, so it reads the admin listing (which
  // includes inactive ones), not the public `/api/services` that filters them.
  const load = useCallback(
    () => api.get('/api/admin/services').then(setServices).catch(() => setServices([])),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const closeModal = () => {
    setAdding(false);
    setEditing(null);
    setIconFile(null);
  };

  const create = async () => {
    setSaving(true);
    try {
      const service = await api.post('/api/admin/services', draft);
      if (iconFile) await api.upload(`/api/admin/services/${service.id}/icon`, iconFile);
      setDraft({ name: '', description: '' });
      setIconFile(null);
      setAdding(false);
      toast.success('Service category added.');
      load();
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/services/${editing.id}`, {
        name: editing.name,
        description: editing.description,
        isActive: !!editing.is_active,
      });
      if (iconFile) await api.upload(`/api/admin/services/${editing.id}/icon`, iconFile);
      setEditing(null);
      setIconFile(null);
      toast.success('Service updated.');
      load();
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/api/admin/services/${s.id}`, { isActive: !s.is_active });
      toast.success(!s.is_active ? 'Service enabled.' : 'Service hidden from booking.');
      load();
    } catch (err) {
      toast.error(err);
    }
  };

  const remove = async () => {
    try {
      await api.del(`/api/admin/services/${confirmDelete.id}`);
      setConfirmDelete(null);
      toast.success('Service deleted.');
      load();
    } catch (err) {
      toast.error(err);
      setConfirmDelete(null);
    }
  };

  if (!services) return <Loading rows={2} />;

  return (
    <>
      <PageHeader title="Service categories" subtitle="What nurses can offer and patients can book.">
        <button className="btn btn-primary" onClick={() => setAdding(true)}>
          <Icon name="plus" /> Add service
        </button>
      </PageHeader>

      {services.length === 0 ? (
        <Empty
          art="documents"
          title="No service categories yet"
          action={
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              Add the first service
            </button>
          }
        >
          Categories define what nurses can offer and what patients can book.
        </Empty>
      ) : (
        <div className="grid grid-3">
          {services.map((s, i) => (
            <article
              key={s.id}
              className="card card-pad anim-rise"
              style={{ '--i': i, ...(s.is_active ? {} : { opacity: 0.65 }) }}
            >
              <div className="row-tight">
                <span className="icon-tile icon-tile-lg" aria-hidden="true">
                  <ServiceIcon service={s} size={26} />
                </span>
                <div className="grow">
                  <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                    <span className="t-title truncate">{s.name}</span>
                    {!s.is_active && <span className="pill pill-warning">Hidden</span>}
                  </div>
                  <div className="tiny dim">
                    {s.nurse_count} nurse{s.nurse_count === 1 ? '' : 's'} · {s.booking_count} booking
                    {s.booking_count === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              {s.description && <p className="small muted mt-2 clamp-3">{s.description}</p>}
              <div className="row mt-3">
                <button className="btn btn-sm" onClick={() => setEditing({ ...s })}>
                  <Icon name="edit" /> Edit
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => toggleActive(s)}>
                  <Icon name={s.is_active ? 'ban' : 'check'} />
                  {s.is_active ? 'Hide' : 'Enable'}
                </button>
                <div className="spacer" />
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => setConfirmDelete(s)}
                  title={s.booking_count > 0 ? 'Has bookings — hide it instead' : 'Delete'}
                  aria-label={`Delete ${s.name}`}
                >
                  <Icon name="trash" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {(adding || editing) && (
        <Modal
          title={adding ? 'Add a service category' : `Edit ${editing.name}`}
          onClose={closeModal}
          footer={
            <>
              <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={(adding ? !draft.name.trim() : !editing.name.trim()) || saving}
                onClick={adding ? create : save}
              >
                {saving ? <span className="spinner" /> : adding ? 'Create' : 'Save changes'}
              </button>
            </>
          }
        >
          <Field label="Name" id="s-name">
            <input
              id="s-name"
              type="text"
              value={adding ? draft.name : editing.name}
              onChange={(e) =>
                adding
                  ? setDraft((d) => ({ ...d, name: e.target.value }))
                  : setEditing((x) => ({ ...x, name: e.target.value }))
              }
              placeholder="Dialysis support"
            />
          </Field>
          <Field label="Icon image" hint="Upload a square image (JPG, PNG or WebP). Optional.">
            <ImagePicker
              onPick={setIconFile}
              existingUrl={editing ? serviceIconUrl(editing.icon_image) : null}
              label="Upload icon"
            />
          </Field>
          <Field label="Description" id="s-desc">
            <textarea
              id="s-desc"
              value={(adding ? draft.description : editing.description) || ''}
              onChange={(e) =>
                adding
                  ? setDraft((d) => ({ ...d, description: e.target.value }))
                  : setEditing((x) => ({ ...x, description: e.target.value }))
              }
            />
          </Field>
        </Modal>
      )}

      {confirmDelete && (
        <Modal
          title={`Delete ${confirmDelete.name}?`}
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={remove}>
                Delete permanently
              </button>
            </>
          }
        >
          {confirmDelete.booking_count > 0 ? (
            <p className="muted">
              <strong>{confirmDelete.name}</strong> has {confirmDelete.booking_count} booking
              {confirmDelete.booking_count === 1 ? '' : 's'} in its history, so it cannot be deleted — the
              server will refuse. Hide it instead to stop new bookings.
            </p>
          ) : (
            <p className="muted">
              This permanently removes <strong>{confirmDelete.name}</strong> and unlinks it from{' '}
              {confirmDelete.nurse_count} nurse{confirmDelete.nurse_count === 1 ? '' : 's'}. This cannot be
              undone.
            </p>
          )}
        </Modal>
      )}
    </>
  );
}
